import { useLocalePath } from "../../hooks/useLocalePath";
import { PageMeta } from "../../seo/PageMeta";
import { ApiErrorState } from "../shared/ApiErrorState";
import { CommonLinkButton } from "../shared/CommonLinkButton";
import { TeamLogo } from "../shared/TeamLogo";
import { TeamMatchHistory } from "./TeamMatchHistory";
import { TeamRosterSection } from "./teamRoster";
import { useTeamPage } from "./hooks/useTeamPage";

const pageShellClassName =
  "mx-auto box-border flex w-full min-w-0 max-w-[60rem] flex-col gap-[1.35rem] self-center px-[clamp(0.75rem,2.5vw,1.25rem)] pt-40 pb-16 max-md:gap-[1.15rem] max-md:px-[0.85rem] max-md:pt-36 max-md:pb-12";

const statusTextClassName = "m-0 font-shentox text-white/72";

export const TeamPage = () => {
  const { localized } = useLocalePath();
  const {
    copy,
    strings,
    team,
    description,
    loading,
    error,
    overview,
    tab,
    setTab,
    tabs,
    historySeason,
    setHistorySeason,
    historySeasons,
    filteredHistory,
    primaryRoster,
    substitutes,
    championshipCount,
    placements,
    matchHistory,
    formatSeasonLabel,
    placeLabel,
  } = useTeamPage();

  if (loading) {
    return (
      <div className={pageShellClassName}>
        <p className={statusTextClassName}>{copy.LOADING}</p>
      </div>
    );
  }

  if (error || !overview || !team) {
    return (
      <div className={pageShellClassName}>
        <ApiErrorState message={error ?? copy.NOT_FOUND} />
      </div>
    );
  }

  return (
    <div className={pageShellClassName}>
      <PageMeta
        title={`${team.name} · ${strings.SEO.TEAMS.TITLE}`}
        description={description || strings.SEO.TEAM.DESCRIPTION.replace("{team}", team.name)}
        pathname={localized(`/fpl/teams/${encodeURIComponent(team.id)}`)}
        internalPath={`/fpl/teams/${team.id}`}
      />

      <header className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-[1.35rem] rounded-[var(--surface-card-radius)] border border-[var(--surface-card-border)] bg-[var(--surface-card-bg)] p-[1.35rem_1.5rem] max-md:grid-cols-1 max-md:justify-items-center max-md:gap-4 max-md:p-[1.15rem_1rem] max-md:text-center">
        <TeamLogo
          teamId={team.id}
          className="h-[clamp(5.5rem,16vw,8rem)] w-[clamp(5.5rem,16vw,8rem)] rounded-[0.9rem] border-2 border-[#333] object-cover shadow-[0_4px_15px_rgba(0,0,0,0.45)] max-md:h-[5.5rem] max-md:w-[5.5rem]"
          alt={`${team.name} logo`}
          linkToTeam={false}
        />
        <div className="flex min-w-0 flex-col gap-[0.85rem] max-md:items-center">
          <h1 className="fh-title-entity">{team.name}</h1>
          {description ? (
            <p className="fh-body max-w-[38rem] text-white/82 max-md:text-center">{description}</p>
          ) : null}
          <div
            className="flex flex-wrap gap-3 max-md:justify-center"
            aria-label={copy.SOCIALS_LABEL}
          >
            {team.website && (
              <CommonLinkButton text="" link={team.website} icon="icons/globe.png" />
            )}
            {team.twitter && (
              <CommonLinkButton text="" link={team.twitter} icon="icons/twitter-logo.png" />
            )}
            {team.discord && (
              <CommonLinkButton text="" link={team.discord} icon="icons/dc-logo.png" />
            )}
          </div>
          <dl className="mt-[0.35rem] mb-0 grid w-full grid-cols-3 gap-3 max-md:gap-2 max-[480px]:grid-cols-1">
            <div className="rounded-xl border border-white/8 bg-white/4 p-[0.75rem_0.9rem] max-md:p-[0.6rem_0.5rem]">
              <dt className="fh-meta mb-1 max-md:text-[0.62rem]">{copy.HIGHLIGHT_SEASONS}</dt>
              <dd className="fh-stat max-md:text-[1.05rem]">{placements.length}</dd>
            </div>
            <div className="rounded-xl border border-white/8 bg-white/4 p-[0.75rem_0.9rem] max-md:p-[0.6rem_0.5rem]">
              <dt className="fh-meta mb-1 max-md:text-[0.62rem]">{copy.HIGHLIGHT_TITLES}</dt>
              <dd className="fh-stat max-md:text-[1.05rem]">{championshipCount}</dd>
            </div>
            <div className="rounded-xl border border-white/8 bg-white/4 p-[0.75rem_0.9rem] max-md:p-[0.6rem_0.5rem]">
              <dt className="fh-meta mb-1 max-md:text-[0.62rem]">{copy.HIGHLIGHT_MATCHES}</dt>
              <dd className="fh-stat max-md:text-[1.05rem]">{matchHistory.length}</dd>
            </div>
          </dl>
        </div>
      </header>

      <div
        className="inline-flex w-fit max-w-full flex-wrap gap-[0.35rem] rounded-xl border border-white/8 bg-white/4 p-[0.3rem] max-md:w-full"
        role="tablist"
        aria-label={copy.TABS_LABEL}
      >
        {tabs.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            id={`team-tab-${item.id}`}
            aria-selected={tab === item.id}
            aria-controls={`team-panel-${item.id}`}
            className={`cursor-pointer rounded-[0.55rem] border-0 bg-transparent px-4 py-[0.55rem] font-shentox text-[0.92rem] font-semibold tracking-[0.04em] transition-[background,color] duration-150 hover:text-white max-md:flex-1 max-md:px-3 max-md:py-[0.65rem] max-md:text-center max-md:text-[0.82rem] max-[480px]:flex-[1_1_calc(50%-0.25rem)] ${
              tab === item.id ? "bg-[rgba(28,152,217,0.2)] text-white" : "text-white/65"
            }`}
            onClick={() => {
              setTab(item.id);
            }}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div
        className="min-w-0"
        role="tabpanel"
        id={`team-panel-${tab}`}
        aria-labelledby={`team-tab-${tab}`}
      >
        {tab === "roster" ? (
          <div className="[&>section+section]:mt-7 [&>section]:mt-0">
            <TeamRosterSection title={copy.PRIMARY_ROSTER} players={primaryRoster} />
            <TeamRosterSection title={copy.SUBSTITUTES} players={substitutes} isSubstitutes />
            {!primaryRoster.length && !substitutes.length ? (
              <p className={statusTextClassName}>{copy.NO_ROSTER}</p>
            ) : null}
          </div>
        ) : null}

        {tab === "history" ? (
          <div className="flex flex-col gap-5">
            {historySeasons.length > 0 ? (
              <label
                className="flex w-full max-w-[18rem] flex-col gap-[0.45rem] font-shentox text-[0.85rem] font-semibold tracking-[0.05em] text-white/70 uppercase max-md:w-full"
                htmlFor="team-history-season"
              >
                <span>{copy.FILTER_SEASON}</span>
                <select
                  id="team-history-season"
                  className="appearance-none rounded-[0.55rem] border border-white/12 bg-white/4 px-[0.85rem] py-[0.65rem] font-shentox text-[0.95rem] font-medium tracking-[0.02em] text-white normal-case"
                  value={historySeason}
                  onChange={(event) => {
                    setHistorySeason(event.target.value);
                  }}
                >
                  <option value="all">{copy.ALL_SEASONS}</option>
                  {historySeasons.map((season) => (
                    <option key={season} value={season}>
                      {formatSeasonLabel(season)}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            <TeamMatchHistory teamId={team.id} teamName={team.name} entries={filteredHistory} />
          </div>
        ) : null}

        {tab === "placements" ? (
          <div className="flex flex-col gap-5">
            {placements.length > 0 ? (
              <div className="w-full overflow-x-auto rounded-[var(--surface-card-radius)] border border-[var(--surface-card-border)] bg-[var(--surface-card-bg)]">
                <table className="w-full min-w-[36rem] border-collapse max-md:min-w-[30rem]">
                  <caption className="visually-hidden">{copy.TAB_PLACEMENTS}</caption>
                  <thead>
                    <tr>
                      <th
                        scope="col"
                        className="fh-table-head border-b border-white/8 bg-white/3 px-4 py-[0.9rem] text-left whitespace-nowrap max-md:px-[0.7rem] max-md:py-3"
                      >
                        {copy.COLUMN_SEASON}
                      </th>
                      <th
                        scope="col"
                        className="fh-table-head border-b border-white/8 bg-white/3 px-4 py-[0.9rem] text-left whitespace-nowrap max-md:px-[0.7rem] max-md:py-3"
                      >
                        {copy.COLUMN_PLACE}
                      </th>
                      <th
                        scope="col"
                        className="fh-table-head border-b border-white/8 bg-white/3 px-4 py-[0.9rem] text-left whitespace-nowrap max-md:px-[0.7rem] max-md:py-3"
                      >
                        {copy.COLUMN_RECORD}
                      </th>
                      <th
                        scope="col"
                        className="fh-table-head border-b border-white/8 bg-white/3 px-4 py-[0.9rem] text-left whitespace-nowrap max-md:px-[0.7rem] max-md:py-3"
                      >
                        {copy.COLUMN_GAMES}
                      </th>
                      <th
                        scope="col"
                        className="fh-table-head border-b border-white/8 bg-white/3 px-4 py-[0.9rem] text-left whitespace-nowrap max-md:px-[0.7rem] max-md:py-3"
                      >
                        {copy.COLUMN_RESULT}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="[&_tr:last-child_td]:border-b-0 [&_tr:last-child_th]:border-b-0">
                    {placements.map((row) => (
                      <tr
                        key={row.season}
                        className={row.isChampion ? "bg-[rgba(200,170,110,0.08)]" : undefined}
                      >
                        <th
                          scope="row"
                          className="border-b border-white/8 px-4 py-[0.9rem] text-left font-shentox font-bold whitespace-nowrap text-white max-md:px-[0.7rem] max-md:py-3"
                        >
                          {formatSeasonLabel(row.season)}
                        </th>
                        <td className="border-b border-white/8 px-4 py-[0.9rem] text-left font-shentox whitespace-nowrap text-white/85 max-md:px-[0.7rem] max-md:py-3">
                          {placeLabel(row.place)}
                        </td>
                        <td className="border-b border-white/8 px-4 py-[0.9rem] text-left font-shentox whitespace-nowrap text-white/85 max-md:px-[0.7rem] max-md:py-3">
                          {row.wins}–{row.losses}
                        </td>
                        <td className="border-b border-white/8 px-4 py-[0.9rem] text-left font-shentox whitespace-nowrap text-white/85 max-md:px-[0.7rem] max-md:py-3">
                          {row.gameWins}–{row.gameLosses}
                        </td>
                        <td className="border-b border-white/8 px-4 py-[0.9rem] text-left font-shentox whitespace-nowrap text-white/85 max-md:px-[0.7rem] max-md:py-3">
                          {row.isChampion ? (
                            <span className="inline-flex items-center rounded-full bg-[rgba(200,170,110,0.18)] px-[0.55rem] py-1 font-shentox text-[0.78rem] font-bold tracking-[0.06em] text-[#e8c872] uppercase">
                              {copy.CHAMPION}
                            </span>
                          ) : (
                            <span className="text-[0.9rem] text-white/50">
                              {copy.REGULAR_SEASON}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className={statusTextClassName}>{copy.NO_PLACEMENTS}</p>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};
